<template>
    <transition name="fade">
        <div :id="id" ref="elem-dropdown" class="elem-dropdown fast" v-show="showDropdown">
            <transition name="nudge-down">
                <div
                    class="elem-dropdown-inner fast"
                    v-show="showDropdown"
                    :style="{ padding, border, maxHeight, borderRadius }"
                    ref="inner"
                >
                    <slot :isOpen="showDropdown" @close="hide"></slot>
                </div>
            </transition>
        </div>
    </transition>
</template>

<script>
    import { createPopper } from '@popperjs/core';
    import { strings, constants, dom } from '../utils';

    export default {
        name: 'ElemDropdown',
        data() {
            return {
                showDropdown: false,
                triggerElem: undefined,
                popper: undefined,
            };
        },
        props: {
            parentalControl: {
                // parent shows/hides dropdown
                type: Boolean,
                default: false,
            },
            parentalShow: {
                type: Boolean,
                default: false,
            },
            triggerSelector: {
                required: true,
            },
            closeOnClick: {
                type: Boolean,
                default: true,
            },
            position: {
                // Popper.js position
                type: String,
                default: 'bottom-start',
            },
            offset: {
                // Popper.js offset
                type: Array,
                default() {
                    return [0, 0];
                },
            },
            borderRadius: {
                type: String,
                default: '0.375rem',
            },
            border: {
                type: String,
                default: '1px solid #d2d6dc',
            },
            padding: {
                type: String,
                default: '0.25em',
            },
            maxHeight: {
                type: String,
                default: '',
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            fullWidth: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            id() {
                return strings.random();
            },
        },
        methods: {
            async toggle(skipFocus = true) {
                if (this.disabled) return;

                this.showDropdown = !this.showDropdown;

                if (this.showDropdown) {
                    await this.$nextTick();
                    this.popper.forceUpdate();
                    if (!skipFocus) {
                        let elems = dom.focusableElements(this.$refs.inner, true);
                        if (elems.length) elems[0].focus();
                    }
                }
            },
            hide(e) {
                if ((e && e.target.closest(this.triggerSelector)) || e.target.closest('.flatpickr-calendar')) return;

                if (this.closeOnClick || !e.target.closest(`#${this.id}`)) {
                    this.showDropdown = false;
                }
            },
            hideIfEsc(e) {
                if (e.which === constants.KEY_ESCAPE) {
                    this.showDropdown = false;
                    this.triggerElem.focus();
                }
            },
            triggerKeydown(e) {
                if (e.which === constants.KEY_SPACE) {
                    e.preventDefault();
                } else if (e.which === constants.KEY_ENTER) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.toggle(false);
                }
            },
            triggerKeyup(e) {
                if (e.which === constants.KEY_SPACE) {
                    this.toggle(false);
                }
            },
        },
        async mounted() {
            await this.$nextTick();
            this.triggerElem = document.querySelector(this.triggerSelector);
            if (!this.triggerElem) return;

            if (this.parentalControl) {
                if (this.parentalShow) this.toggle();
            } else {
                this.triggerElem.addEventListener('click', this.toggle);
                this.triggerElem.addEventListener('keydown', this.triggerKeydown);
                this.triggerElem.addEventListener('keydown', this.triggerKeyup);
            }

            let modifiers = [
                {
                    name: 'offset',
                    options: {
                        offset: this.offset,
                    },
                },
            ];

            if (this.fullWidth) {
                modifiers.push({
                    name: 'sameWidth',
                    enabled: true,
                    fn: ({ state }) => {
                        state.styles.popper.width = `${state.rects.reference.width}px`;
                    },
                    phase: 'beforeWrite',
                    requires: ['computeStyles'],
                });
            }

            this.popper = createPopper(this.triggerElem, this.$refs['elem-dropdown'], {
                placement: this.position,
                modifiers,
            });

            // Add screen-reader-friendly attributes to the trigger
            this.triggerElem.setAttribute('role', 'button');
            this.triggerElem.setAttribute('aria-haspopup', 'true');
            this.triggerElem.setAttribute('aria-expanded', 'false');
        },
        beforeDestroy() {
            if (this.popper) this.popper.destroy();
            if (this.triggerElem) {
                this.triggerElem.removeEventListener('click', this.toggle);
                this.triggerElem.removeEventListener('keydown', this.triggerKeydown);
                this.triggerElem.removeEventListener('keyup', this.triggerKeyup);
            }
        },
        watch: {
            parentalShow(show) {
                if (!this.parentalControl) return;

                if (show !== this.showDropdown) {
                    this.toggle();
                }
            },
            showDropdown(show) {
                if (show) {
                    this.triggerElem.classList.add('active');
                    //this.triggerElem.addEventListener('keydown', this.triggerKeydown);
                    //this.triggerElem.addEventListener('keyup', this.triggerKeyup);
                    document.addEventListener('click', this.hide);
                    document.addEventListener('keydown', this.hideIfEsc);
                    this.$emit('show');
                } else {
                    this.triggerElem.classList.remove('active');
                    document.removeEventListener('click', this.hide);
                    document.removeEventListener('keydown', this.hideIfEsc);
                    this.$emit('hide');
                }

                this.triggerElem.setAttribute('aria-expanded', show ? 'true' : 'false');
                this.$store.commit('page/dropdownOpen', show);
            },
            disabled(value) {
                if (value && this.showDropdown) {
                    this.toggle();
                }
            },
        },
    };
</script>

<style lang='scss' scoped>
    .elem-dropdown {
        z-index: 50;
    }

    ::v-deep .elem-dropdown-inner {
        @include box-shadow-small;
        @include border-radius-small;

        background: $color-white;
        overflow-y: auto;
        user-select: none;

        ul {
            padding: 0;
            margin: 0;
        }
    }
</style>